/**
 * What each kind of carryable object is: what to call it, how big it is when the profile
 * does not say, and whether it fits in a pocket (design D1).
 *
 * Pure data — no renderer, no physics. The engine reads `size` when it builds a body and
 * `label` when the HUD names an item; the pocket reads `stowable` and nothing else.
 *
 * `stowable` is a property of the kind rather than a size threshold deliberately. "Small
 * enough to pocket" is a decision about what an object *is*, and a threshold would let a
 * profile author a 10 cm cardboard box and silently make it pocketable — a bug nobody
 * would find except a player.
 */

import type { ObjectKind, Vec3 } from './profile/types';

export type ObjectKindSpec = {
  /** Shown when the game names the item — the pocket HUD reads this. */
  label: string;
  /** Full extents when the profile does not give a size. */
  size: Vec3;
  /** Whether it fits in a pocket. */
  stowable: boolean;
};

export const OBJECT_KINDS: Record<ObjectKind, ObjectKindSpec> = {
  'cardboard-box': {
    label: 'Cardboard box',
    // The size every existing level was laid out against: changing it would move the
    // boxes in the house profiles, not just the ones that omit a size.
    size: { x: 0.45, y: 0.45, z: 0.45 },
    stowable: false,
  },
  screwdriver: {
    label: 'Screwdriver',
    // Lying flat, handle to tip along z — a tool on a table, not a stick on its end.
    size: { x: 0.04, y: 0.04, z: 0.24 },
    stowable: true,
  },
  'robot-dog-body': {
    label: 'Robot dog',
    // Sized for the whole standing dog, torso and the leg space beneath it, not just
    // the torso — a bare body sits exactly as high as a finished one, and nothing moves
    // when the last leg goes on (design D2).
    size: { x: 0.34, y: 0.42, z: 0.55 },
    stowable: false,
  },
  'robot-dog-leg': {
    label: 'Leg',
    size: { x: 0.06, y: 0.18, z: 0.06 },
    stowable: true,
  },
};

/**
 * Objects rest a hair above whatever they stand on — floor or table top — so physics
 * settles them down onto it instead of starting the frame interpenetrating it.
 */
export const PLACEMENT_CLEARANCE = 0.02;

/** Whether an item of this kind can go in the pocket rather than only in the hands. */
export function isStowable(kind: ObjectKind): boolean {
  return OBJECT_KINDS[kind].stowable;
}
